import classNames from 'classnames';
import { Fragment, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa6';

import { useGetEconomySettingsChartOfAccountsQuery } from '@client/api';
import { Button, FormField, Input, Select, Skeleton, Table, TableBody, TableCell, TableRow } from '@client/toolkit';

import { EconomyCardLoadError } from './economy-card-load-error.component';
import { EconomyCard } from './economy-card.component';
import { EconomyEditableCard } from './economy-editable-card.component';
import { EconomyRowActions } from './economy-row-actions.component';
import { EconomyTableHead } from './economy-table-head.component';
import {
  ECONOMY_FIELD_SIZE,
  ECONOMY_HINT_CLASS,
  ECONOMY_VALUE_CLASS,
} from '../economy-theme';
import { COST_CLASSIFICATIONS } from '../mock/portfolio-economy.mock';
import {
  commonCostChartSchema,
  CommonCostChartFormPath,
  CommonCostChartFormValues as FormValues,
  createEmptyChartLine,
  toCommonCostChartFormValues,
} from '../portfolio-economy-settings.form';

/** Width hints; the standard table lays out on content, so these only steer it. */
const COLUMNS = [
  { key: 'account', className: 'w-[150px]' },
  { key: 'lineName', className: 'w-[320px]' },
  { key: 'subCategory', className: 'w-[230px]' },
  { key: 'classification', className: 'w-[160px]' },
  { key: 'actions', className: 'w-[120px]', align: 'right' as const },
] as const;

/** The tenant's own felleskost categories, as the grouping key and the row select's options. */
interface ChartCategory {
  id: string;
  name: string;
}

interface ChartRowProps {
  index: number;
  editing: boolean;
  isFirst: boolean;
  isLast: boolean;
  categories: readonly ChartCategory[];
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (index: number) => void;
}

function ChartLineRow({
  index,
  editing,
  isFirst,
  isLast,
  categories,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ChartRowProps) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const line = useWatch({ control, name: `commonCostChart.${index}` });
  const accountNumber = line?.accountNumber;
  const row = typeof accountNumber === 'string' && accountNumber.trim() !== '' ? accountNumber : String(index + 1);

  /** The uniqueness test spans rows, so a resolved collision needs the array re-run. */
  const revalidateAccountNumbers = () => {
    if (getFieldState('commonCostChart', formState).error != null) void trigger('commonCostChart');
  };

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{accountNumber}</span>
        ) : (
          <FormField<FormValues, CommonCostChartFormPath>
            name={`commonCostChart.${index}.accountNumber`}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(event) => {
                  field.onChange(event);
                  revalidateAccountNumbers();
                }}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.commonCostChart.rowFields.account', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>{line?.name}</span>
        ) : (
          <FormField<FormValues, CommonCostChartFormPath>
            name={`commonCostChart.${index}.name`}
            render={({ field }) => (
              <Input
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.commonCostChart.rowFields.lineName', { row })}
              />
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {/* Only offered while editing: read-only rows already sit under their group heading. */}
        {!editing ? (
          <span aria-hidden />
        ) : (
          <FormField<FormValues, CommonCostChartFormPath>
            name={`commonCostChart.${index}.costCategoryId`}
            render={({ field }) => (
              <Select
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.commonCostChart.rowFields.subCategory', { row })}
              >
                {/* Names come from the database, not from i18n: the categories are tenant-owned
                    rows with free-text names, so there is no key to translate. */}
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            )}
          />
        )}
      </TableCell>

      <TableCell>
        {!editing ? (
          <span className={ECONOMY_VALUE_CLASS}>
            {line?.classification && t(`classification.${line.classification}`)}
          </span>
        ) : (
          <FormField<FormValues, CommonCostChartFormPath>
            name={`commonCostChart.${index}.classification`}
            render={({ field }) => (
              <Select
                {...field}
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.commonCostChart.rowFields.classification', { row })}
              >
                {COST_CLASSIFICATIONS.map((classification) => (
                  <option key={classification} value={classification}>
                    {t(`classification.${classification}`)}
                  </option>
                ))}
              </Select>
            )}
          />
        )}
      </TableCell>

      <TableCell align="right">
        {editing && (
          <EconomyRowActions
            section="settings.commonCostChart"
            row={row}
            isFirst={isFirst}
            isLast={isLast}
            onMoveUp={() => onMoveUp(index)}
            onMoveDown={() => onMoveDown(index)}
            onRemove={() => onRemove(index)}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

/** Reads the form the card owns, so it has to live inside its `FormProvider`. */
function CommonCostChartTable({
  editing,
  categories,
}: {
  editing: boolean;
  categories: readonly ChartCategory[];
}) {
  const { t } = useTranslation('economy');
  const { control, trigger, getFieldState, formState } = useFormContext<FormValues>();

  const { fields, remove, swap } = useFieldArray<FormValues, 'commonCostChart'>({
    control,
    name: 'commonCostChart',
  });
  // The `?? []` is memoised rather than inlined into the dependency array: a fresh literal every
  // render defeats the useMemo below, which exhaustive-deps flags.
  const watchedLines = useWatch({ control, name: 'commonCostChart' });
  const lines = useMemo(() => watchedLines ?? [], [watchedLines]);

  // Only the first group starts open, as in the design; the rest are one click away. Keyed on the
  // category id, which is a uuid from the server rather than a member of a fixed enum.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(categories.length > 0 ? [categories[0].id] : []),
  );

  const toggleGroup = (id: string) =>
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Rows keep their flat form index — reordering and removal address the array, not the
   * group — so each group carries the indices that belong to it rather than a copy of them.
   */
  const groups = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        name: category.name,
        indices: lines.reduce<number[]>((acc, line, index) => {
          if (line?.costCategoryId === category.id) acc.push(index);
          return acc;
        }, []),
      })),
    [categories, lines],
  );

  const revalidateAfter = (mutate: () => void) => {
    const hadError = getFieldState('commonCostChart', formState).error != null;
    mutate();
    if (hadError) void trigger('commonCostChart');
  };

  return (
    <>
      {/* No `size`: the toolkit `Table` never puts it on `TableContext`, so the cells fall
          back to `medium` whatever is passed. Asking for `xs` here only reads as if the
          tables were compact. */}
      <Table className="w-full rounded-md">
        <EconomyTableHead section="settings.commonCostChart" columns={COLUMNS} />

        {/* One body so the standard row dividers run unbroken across the groups. */}
        <TableBody>
          {groups.map((group) => {
            const isOpen = openGroups.has(group.id);
            return (
              <Fragment key={group.id}>
                <TableRow verticalAlign="middle" className="!bg-surface-elevated/40">
                  <TableCell colSpan={COLUMNS.length}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50 transition-colors hover:text-white/80"
                    >
                      <FaChevronDown
                        className={classNames(
                          'h-3 w-3 shrink-0 transition-transform duration-200',
                          !isOpen && '-rotate-90',
                        )}
                      />
                      <span className="flex-1 text-left">{group.name}</span>
                      <span className="font-normal normal-case tracking-normal">{group.indices.length}</span>
                    </button>
                  </TableCell>
                </TableRow>

                {isOpen &&
                  group.indices.map((index, positionInGroup) => (
                    <ChartLineRow
                      key={fields[index]?.id ?? index}
                      index={index}
                      editing={editing}
                      categories={categories}
                      isFirst={positionInGroup === 0}
                      isLast={positionInGroup === group.indices.length - 1}
                      onMoveUp={() => revalidateAfter(() => swap(index, group.indices[positionInGroup - 1]))}
                      onMoveDown={() => revalidateAfter(() => swap(index, group.indices[positionInGroup + 1]))}
                      onRemove={() => revalidateAfter(() => remove(index))}
                    />
                  ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {fields.length === 0 && <p className="py-4 text-sm text-secondary">{t('settings.commonCostChart.empty')}</p>}

      <p className={`mt-3 px-3 ${ECONOMY_HINT_CLASS}`}>{t('settings.commonCostChart.hint')}</p>
    </>
  );
}

/** Header action; writes through the form so the table's field array picks the row up. */
function AddChartLineButton({ categories }: { categories: readonly ChartCategory[] }) {
  const { t } = useTranslation('economy');
  const { getValues, setValue } = useFormContext<FormValues>();

  return (
    <Button
      type="button"
      color="primary-dark"
      size="sm"
      // A line must belong to a category, and the categories come from the server. With none there
      // is nothing valid to create.
      disabled={categories.length === 0}
      onClick={() =>
        setValue(
          'commonCostChart',
          [...getValues('commonCostChart'), createEmptyChartLine(categories[0].id)],
          { shouldDirty: true },
        )
      }
    >
      {t('settings.commonCostChart.add')}
    </Button>
  );
}

export interface CommonCostChartCardProps {
  /**
   * Omitted for now, which renders the card read-only. Wire it when EXIO-2988 ships the PUT:
   * EconomyEditableCard reports success unconditionally, so a card that cannot persist must not
   * offer Rediger. Raised in review on PR #625.
   */
  onSave?: (values: FormValues) => void;
}

/**
 * The portfolio-wide felleskost chart of accounts — the full set a building can switch on.
 * Lines are grouped by their cost category, which is editable, so a line can be moved between
 * groups.
 *
 * The card fetches its own data, as GroupCard does: the Innstillinger tab has no page-level fetch.
 */
export function CommonCostChartCard({ onSave }: CommonCostChartCardProps) {
  const { t } = useTranslation('economy');

  const { data, isLoading, isError } = useGetEconomySettingsChartOfAccountsQuery();

  const categories = useMemo(
    () => (data?.commonCost.categories ?? []).map(({ id, name }) => ({ id, name })),
    [data],
  );
  const defaultValues = useMemo(() => toCommonCostChartFormValues(data), [data]);

  if (isLoading) {
    return (
      <EconomyCard title={t('settings.commonCostChart.title')}>
        <div data-testid="common-cost-chart-skeleton">
          <Skeleton variant="text" height="h-4" width="w-64" />
        </div>
      </EconomyCard>
    );
  }

  // An empty chart and a failed request are different facts. Rendering the first for the second
  // tells the customer their chart is empty, which is a claim we have not established.
  if (isError || data == null) {
    return (
      <EconomyCardLoadError
        title={t('settings.commonCostChart.title')}
        message={t('settings.commonCostChart.loadError')}
      />
    );
  }

  return (
    <EconomyEditableCard<FormValues>
      title={t('settings.commonCostChart.title')}
      defaultValues={defaultValues}
      schema={commonCostChartSchema}
      onSave={onSave}
      editActions={<AddChartLineButton categories={categories} />}
    >
      {(editing) => <CommonCostChartTable editing={editing} categories={categories} />}
    </EconomyEditableCard>
  );
}

export default CommonCostChartCard;
